/**
 * 1.8.1: the live player's choices, kept pure so they can be tested (LivePlayer.tsx).
 */

/** How many segments a recording in progress is joined with: the live edge, about 12 s of the 4 s segment proxy. */
export const LIVE_EDGE_SEGMENTS = 3;
/** Media further behind the playhead than this is removed from the buffer (a recording of hours fills it otherwise). */
export const EVICT_BEHIND_SECONDS = 120;
/** And only once this much more has gathered, so the buffer is not trimmed after every segment. */
const EVICT_SLACK_SECONDS = 10;

/** The media segments a playlist lists, in order. */
export const playlistSegments = (text: string): string[] =>
  text.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('#'));

/**
 * 1.9.1: the seconds the named segments last (their #EXTINF), for the time skipped when a recording is joined at its
 * live edge: the LivePlayer's position 0 is the first segment it appended, so the recording's own time is that plus
 * the skipped seconds (Log's "at the playhead" needs the recording's time).
 */
export function segmentsSeconds(playlist: string, names: readonly string[]): number {
  if (!names.length) return 0;
  const want = new Set(names);
  let sum = 0;
  let pending: number | null = null;
  for (const raw of playlist.split('\n')) {
    const l = raw.trim();
    if (l.startsWith('#EXTINF:')) pending = Number.parseFloat(l.slice(8)) || 0;
    else if (l && !l.startsWith('#')) { if (want.has(l) && pending != null) sum += pending; pending = null; }
  }
  return sum;
}

/**
 * What to do with a playlist's segments: `fetch` the new ones in order; `skip` the ones before the live edge (the first
 * read of a recording in progress joins at its last LIVE_EDGE_SEGMENTS), which the caller marks as done without
 * fetching. A finished recording, or a later read, skips nothing. `done`: the segments appended or skipped already.
 */
export function segmentsToAppend(
  playlist: string, done: ReadonlySet<string>, opts: { firstRead: boolean; recording: boolean },
): { fetch: string[]; skip: string[] } {
  const fresh = playlistSegments(playlist).filter((n) => !done.has(n));
  if (!opts.firstRead || !opts.recording || fresh.length <= LIVE_EDGE_SEGMENTS) return { fetch: fresh, skip: [] };
  return { fetch: fresh.slice(-LIVE_EDGE_SEGMENTS), skip: fresh.slice(0, -LIVE_EDGE_SEGMENTS) };
}

/** Where to remove buffered media up to (from the buffer's start), or null when nothing is far enough behind. */
export function evictionEnd(currentTime: number, bufferedStart: number): number | null {
  const end = currentTime - EVICT_BEHIND_SECONDS;
  return end - bufferedStart > EVICT_SLACK_SECONDS ? end : null;
}

/* 1.9.1: the live edge, shared by the asset page's LivePlayer and the editor's source window (Player). */

/** Go live lands this far before the end: never at the exact end, which would fire `ended`. */
export const GO_LIVE_BACKOFF_SECONDS = 2;
/**
 * The source window counts as at the live edge within this many seconds of what is recorded: 4 s segments, the
 * 5 s refresh of the recorded length and the API's 1.5 s playlist cache can each put it this far behind.
 */
export const EDITOR_EDGE_SECONDS = 12;
/** The asset page's LivePlayer, which reads the playlist every 2 s itself. */
export const PLAYER_EDGE_SECONDS = 8;

/** Where Go live seeks to: just before the end of what is recorded. */
export const goLiveTarget = (recordedSeconds: number): number => Math.max(0, recordedSeconds - GO_LIVE_BACKOFF_SECONDS);

/** How far the playhead is behind the end of what is recorded (not of what is loaded), never negative. */
export const behindSeconds = (recordedSeconds: number, currentTime: number): number => Math.max(0, recordedSeconds - currentTime);

/** Whether the playhead is at the live edge. */
export const atLiveEdge = (recordedSeconds: number, currentTime: number, threshold = EDITOR_EDGE_SECONDS): boolean =>
  behindSeconds(recordedSeconds, currentTime) < threshold;

/** "−0:42", "−12:05", "−1:02:03": how far behind, on the Go live button. */
export function behindText(seconds: number): string {
  const t = Math.max(0, Math.floor(seconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = String(t % 60).padStart(2, '0');
  return `−${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${s}`;
}
